// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { StrictMode, useMemo } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import "@fontsource-variable/sora" // self-hosted variable UI font
import "@fontsource-variable/jetbrains-mono" // self-hosted variable mono font
import "./global.css" // @ts-ignore
import "./tailwind.generated.css" // @ts-ignore
import "@/lib/http-client" // register maintenance-detector interceptor on apiClient
import { AuthenticatedLayout } from "./views/home"
import { AlertModalProvider } from "@/hooks/useAlertModal"
import LoginView from "./views/login"
import HandleGithubCallback from "./views/login/handleGithubCallback"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import ForgesListView from "./views/forges"
import ForgeDetailsView from "./views/forges/details"
import EvolutionCycleDetailsView from "./views/evolutionCycles/details"
import MyCyclesView from "./views/evolutionCycles/myCyclesList"
import { useAllEvolutionCycles } from "@/queries/queries.evolutionCycles"
import { AccountView } from "./views/home"
import RequestCreditsView from "./views/creditRequests"
import AdminCreditRequestsView from "./views/creditRequests/admin"
import { NotFoundView } from "./views/404"
import { ProtectedRoute } from "@/components/ProtectedRoute"
import AgentDetail from "./views/agents/details"
import { FullPageSpinnerWithText } from "@/components/ui/spinner"
import MaintenanceView from "@/components/MaintenanceView/MaintenanceView"
import {
  initObservability,
  SentryErrorBoundary,
} from "@/observability/sentry"
import { SENTRY_DSN, SENTRY_ENVIRONMENT, SENTRY_RELEASE } from "@/config"

// M7: initialise Sentry before React mounts so the global JS error
// handlers (unhandled errors + unhandled promise rejections) are wired
// from the first tick. React render errors are caught by the
// SentryErrorBoundary wrapping the tree below.
initObservability({
  dsn: SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  release: SENTRY_RELEASE,
})

export const withLayout = (component: React.ReactNode) => {
  return <AuthenticatedLayout>{component}</AuthenticatedLayout>
}

// Single app-wide TanStack `QueryClient`, fed to the provider below.
// Hooks resolve it through the React context (`useQueryClient`).
const queryClient = new QueryClient()

export const Providers = ({ children }) => {
  const providers = useMemo(() => {
    return [AlertModalProvider].reverse()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      {providers.reduce((acc, Provider) => {
        return <Provider>{acc}</Provider>
      }, children)}
    </QueryClientProvider>
  )
}

// A brand-new user with no cycles yet lands on "My account", where they
// can top up credits and grab an API key — the empty cycle list would be
// a dead end. On error the cycle list takes over, since its own view
// handles the loading and error states.
const HomeRedirect = () => {
  const { data, isLoading, isError } = useAllEvolutionCycles()

  if (isLoading) {
    return <FullPageSpinnerWithText text="Loading..." />
  }

  const hasNoCycles = !isError && data != null && (data.items?.length ?? 0) === 0
  return <Navigate to={hasNoCycles ? "/profile" : "/evolution-cycles"} replace />
}

// The application's production route table — extracted so tests can
// mount it (through a `<MemoryRouter>`) at the exact paths shipped to
// users, instead of re-declaring the routes locally.
export const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginView />} />
    <Route path="/github/login" element={<HandleGithubCallback />} />

    {/* Everything nested here needs a session; a route is public by
        sitting outside this element, not by being on a list. */}
    <Route element={<ProtectedRoute />}>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/profile" element={withLayout(<AccountView />)} />
      <Route
        path="/request-credits"
        element={withLayout(<RequestCreditsView />)}
      />
      {/* Role-gated (User.Role) — the view itself withholds data + actions
          for a non-admin caller. */}
      <Route
        path="/admin/credit-requests"
        element={withLayout(<AdminCreditRequestsView />)}
      />

      {/* Agents are browsed through their forge, not a flat cross-cycle
          list; only the detail route survives, deep-linked from the cycle
          dashboards and the lineage cards. */}
      <Route path="/agents/:agentId" element={withLayout(<AgentDetail />)} />
      <Route path="/forges" element={withLayout(<ForgesListView />)} />
      <Route
        path="/forges/:forgeId"
        element={withLayout(<ForgeDetailsView />)}
      />
      {/* Global "My Cycles" page — the cross-forge cycle list at
          the canonical list root (detail at /:cycleId). Completes
          the resource layout and kills the legacy /cycles 404. */}
      <Route path="/evolution-cycles" element={withLayout(<MyCyclesView />)} />
      <Route
        path="/evolution-cycles/:cycleId"
        element={withLayout(<EvolutionCycleDetailsView />)}
      />
    </Route>

    <Route path="*" element={<NotFoundView />} />
  </Routes>
)

export const RootComponent = () => {
  return (
    <div className="w-cdw h-cdh">
      <Providers>
        <BrowserRouter>
          <AppRoutes />
          {/* M7 MaintenanceView: mounted AFTER all routes so the overlay
              sits above any routed view when isBackendDown is true. */}
          <MaintenanceView />
        </BrowserRouter>
      </Providers>
    </div>
  )
}
// Guarded so importing this module (e.g. from a test that mounts
// `AppRoutes` through its own harness) never tries to mount into a
// `#root` element that only exists in the shipped `index.html`.
const rootElement = document.getElementById("root")
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <SentryErrorBoundary>
        <RootComponent />
      </SentryErrorBoundary>
    </StrictMode>,
  )
}
